import React, { useState, useEffect, useRef } from 'react';
import { NETWORK_TYPE } from "../../../../../constants";
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Form, 
  Input, 
  Select, 
  Button, 
  Steps, 
  Card, 
  Row, 
  Col, 
  Space, 
  Typography,
  Divider,
  message,
  Checkbox,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  MinusOutlined, 
  UserOutlined, 
  PhoneOutlined, 
  MailOutlined,
  EnvironmentOutlined,
  CreditCardOutlined
} from '@ant-design/icons';
import "./ONAddUser.scss";
import IntlTelInput from "intl-tel-input/react";
import "intl-tel-input/styles";
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import { useDispatch, useSelector } from 'react-redux';
import { appDispatch, RootState } from '../../../../../app/store';
import { addONUser, fetchONMemberShipPlans, fetchONRoles, fetchONSpecialities, registerMembership, resetFetchONRoles, resetFetchONSpecialities, fetchONUserDetails, resetFetchONUserDetails, editONRequest } from '../../../../../features/superAdmin/requestLIsts/requestListsSlice';
import { toast } from 'react-toastify';

const { Title, Text } = Typography;
const { Option } = Select;

interface LicenseInfo {
  country: string; // iso2
  countryId?: number; // id
  countryName?: string;
  state: string; // state_code
  stateId?: number; // id
  stateName?: string;
  licenseNumber: string;
}

interface ContactInfo {
  email: string;
  emailPreference: string;
  pager: string;
  pagerPreference: string;
  primaryPhone: string;
  primaryPhoneCode: string;
  primaryPhonePreference: string;
  secondaryPhone: string;
  secondaryPhoneCode: string;
  secondaryPhonePreference: string;
  practiceName: string;
  practicePosition: string;
  optionalPhone: string;
  optionalPhoneCode: string;
  country: string;
  state: string;
  city: string;
  address: string;
}

type PracticeDetail = {
  practiceName: string;
  practicePosition:string;
  optionalPhone: string;
  optionalPhoneCode: string;
  country: string;
  countryName:string;
  state: string;
  stateName:string;
  city: string;
  cityName:string;
  cityId?: string; // <-- Add cityId
  address: string;
  address2: string;
  zipcode:any;
};

export const ONAddUser = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [licenses, setLicenses] = useState<LicenseInfo[]>([
    { country: '', countryId: undefined, state: '', stateId: undefined, licenseNumber: '' }
  ]);

  const [form] = Form.useForm();

  const location = useLocation();

  const {isEditMode=false, userId="" } = location?.state || {};

  const dispatch = useDispatch<appDispatch>();

  const {
    ONRolesListData,
    isLoading:isONRolesLoading
  } = useSelector((state: RootState) => state.requestLsts.fetchONRoles);
  
  const {
    ONSpecialitiesListData,
    isLoading:isONSpecialitiesLoading
  } = useSelector((state: RootState) => state.requestLsts.fetchONSpecialities);

  const {
    mombershipPlanListData,
    isLoading:isMembershipListLoading
  } = useSelector((state: RootState) => state.requestLsts.fetchONMemberShipPlans);

  const {
    isLoading:userRegisterationIsLoading,
    isSuccess:userRegisterationIsSuccess, 
    message:userRegisterationMessage
  } = useSelector((state: RootState) => state.requestLsts.addONUser);

  const {
    userDetailsData,
    isLoading:isUserDetailsLoading,
  } = useSelector((state: RootState) => state.requestLsts.fetchONUserDetails);

  const {
    isLoading:editRequestIsLoading,
  } = useSelector((state: RootState) => state.requestLsts.editONRequest);


  useEffect(()=>{
    dispatch(fetchONRoles({}))
    dispatch(fetchONMemberShipPlans({ networkTypeID: String(NETWORK_TYPE.OPEN) }))
    
    // Fetch user details if in edit mode
    if (isEditMode && userId) {
      dispatch(fetchONUserDetails(userId))
    }
    
    return ()=>{
      dispatch(resetFetchONRoles())
      dispatch(resetFetchONSpecialities())
      dispatch(resetFetchONUserDetails())
    }
  },[isEditMode, userId])

  const [selectedPrimaryCountry, setSelectedPrimaryCountry] = useState("us");
  const [isPrimaryPhoneNumberValid, setIsPrimaryPhoneNumberValid] = useState(false);
  const primaryNumberRef = React.useRef<any>();
  const [secondaryNumber, setSecondaryNumber] = useState("");
  const [primaryNumber, setPrimaryNumber] = useState("");
  const [selectedSecondaryCountry, setSelectedSecondaryCountry] = useState("us");
  const [isSecondaryPhoneNumberValid, setIsSecondaryPhoneNumberValid] = useState(false);
  const secondaryNumberRef = React.useRef<any>();

  const [countriesList, setCountriesList] = useState<any[]>([]);
  const [stateList, setStateList] = useState<any[]>([]);
  const [cityList, setCityList] = useState<any[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);

  const [licenseCountries, setLicenseCountries] = useState<any[]>([]);
  const [licenseStates, setLicenseStates] = useState<any[][]>([[]]);

  const [selectedRole, setSelectedRole] = useState<any[][]>([[]]);
  const [selectedSpecialty, setSelectedSpecialty] = useState<any[][]>([[]]);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Add at the top, after licenses state
  const [practiceDetails, setPracticeDetails] = useState<PracticeDetail[]>([
    { practiceName: '',practicePosition:'', optionalPhone: '', optionalPhoneCode: '', country: '', countryName : "", state: '', stateName : "" , city: '', cityName:"", address: '', address2: '', zipcode: '' }
  ]);

  // Add after practiceDetails state
  const [practiceStateLists, setPracticeStateLists] = useState<any[][]>([[]]);
  const [practiceCityLists, setPracticeCityLists] = useState<any[][]>([[]]);

  // Practice phone number states
  const [selectedPracticeCountries, setSelectedPracticeCountries] = useState<string[]>(['us']);
  const [isPracticePhoneNumbersValid, setIsPracticePhoneNumbersValid] = useState<boolean[]>([false]);
  const practicePhoneNumberRefs = React.useRef<any[]>([]);

  // Handler to add a new practice detail set
  const addPracticeDetail = () => {
    setPracticeDetails([
      ...practiceDetails,
      { practiceName: '', practicePosition:'',optionalPhone: '', optionalPhoneCode: '', country: '', countryName:"" , state: '', stateName : "", city: '', cityName:"", address: '', address2: '', zipcode: '' }
    ]);
    setPracticeStateLists([...practiceStateLists, []]);
    setPracticeCityLists([...practiceCityLists, []]);
    setSelectedPracticeCountries([...selectedPracticeCountries, 'us']);
    setIsPracticePhoneNumbersValid([...isPracticePhoneNumbersValid, false]);
  };

  // Handler to remove a practice detail set
  const removePracticeDetail = (idx: number) => {
    if (practiceDetails?.length > 1) {
      setPracticeDetails(practiceDetails.filter((_, i) => i !== idx));
      setPracticeStateLists(practiceStateLists.filter((_, i) => i !== idx));
      setPracticeCityLists(practiceCityLists.filter((_, i) => i !== idx));
      setSelectedPracticeCountries(selectedPracticeCountries.filter((_, i) => i !== idx));
      setIsPracticePhoneNumbersValid(isPracticePhoneNumbersValid.filter((_, i) => i !== idx));
      // Reset form fields for the removed row
      form.resetFields([
        `practiceName_${idx}`,  `practicePosition_${idx}`,
        `optionalPhone_${idx}`,
        `country_${idx}`,
        `state_${idx}`,
        `city_${idx}`,
        `address_${idx}`,
        `address2_${idx}`,
        `address3_${idx}`,
        `zipcode_${idx}`
      ]);
    }
  };

  // Handler to update a field in a practice detail set
  const updatePracticeDetail = (idx: number, field: keyof PracticeDetail, value: any) => {
    setHasInteracted(true);
    setPracticeDetails(prev => {
      const newDetails = [...prev];
      if (newDetails[idx]) {
        newDetails[idx] = { ...newDetails[idx], [field]: value };
      }
      return newDetails;
    });
  };

  // Add per-row handlers for country and state
  const handlePracticeCountryChange = (idx: number, countryIso2: string,countryName:string) => {
    setHasInteracted(true);
    updatePracticeDetail(idx, 'country', countryIso2);
    updatePracticeDetail(idx, 'countryName', countryName);
    updatePracticeDetail(idx, 'state', '');
    updatePracticeDetail(idx, 'stateName', '');
    updatePracticeDetail(idx, 'city', '');
    updatePracticeDetail(idx, 'cityName', '');
    updatePracticeDetail(idx, 'cityId', ''); // Fix: use '' instead of undefined
    const countryObj = countriesList.find(c => c.iso2 === countryIso2);
    if (countryObj) {
      GetState(countryObj.id).then((result: any) => {
        setPracticeStateLists(prev => {
          const newArr = [...prev];
          newArr[idx] = result;
          return newArr;
        });
        setPracticeCityLists(prev => {
          const newArr = [...prev];
          newArr[idx] = [];
          return newArr;
        });
      });
    } else {
      setPracticeStateLists(prev => {
        const newArr = [...prev];
        newArr[idx] = [];
        return newArr;
      });
      setPracticeCityLists(prev => {
        const newArr = [...prev];
        newArr[idx] = [];
        return newArr;
      });
    }
  };

  const handlePracticeStateChange = (idx: number, stateCode: string,stateName:string) => {
    setHasInteracted(true);
    updatePracticeDetail(idx, 'state', stateCode);
    updatePracticeDetail(idx, 'stateName', stateName);
    updatePracticeDetail(idx, 'city', '');
    updatePracticeDetail(idx, 'cityName', '');
    updatePracticeDetail(idx, 'cityId', ''); // Fix: use '' instead of undefined
    const countryObj = countriesList.find(c => c.iso2 === practiceDetails[idx].country);
    const stateObj = practiceStateLists[idx]?.find((s: any) => s.state_code === stateCode);
    if (countryObj && stateObj) {
      GetCity(countryObj.id, stateObj.id).then((result: any) => {
        setPracticeCityLists(prev => {
          const newArr = [...prev];
          newArr[idx] = result;
          return newArr;
        });
      });
    } else {
      setPracticeCityLists(prev => {
        const newArr = [...prev];
        newArr[idx] = [];
        return newArr;
      });
    }
  };

  // Practice phone number handlers
  const handlePracticePhoneValidityChange = (idx: number, isValid: boolean) => {
    const newValidities = [...isPracticePhoneNumbersValid];
    newValidities[idx] = isValid;
    setIsPracticePhoneNumbersValid(newValidities);
  };

  const handlePracticePhoneCountryChange = (idx: number, country: any) => {
    if (selectedPracticeCountries[idx] !== country) {
      const newCountries = [...selectedPracticeCountries];
      newCountries[idx] = country;
      setSelectedPracticeCountries(newCountries);

      // Reset phone number and validity for the new country
      form.setFieldsValue({ [`optionalPhone_${idx}`]: "" });
      setIsPracticePhoneNumbersValid(prev => {
        const newValidities = [...prev];
        newValidities[idx] = false;
        return newValidities;
      });
    }
    // Do NOT update state if the country is the same
  };

  // Fetch countries once for license dropdowns
  useEffect(() => {
    GetCountries().then((result: any) => {
      setLicenseCountries(result);
    });
  }, []);

  const updateLicenseFields = (index: number, fields: Partial<LicenseInfo>) => {
    const newLicenses = [...licenses];
    newLicenses[index] = { ...newLicenses[index], ...fields };
    setLicenses(newLicenses);
  };

  // Handler for license country change
  const handleLicenseCountryChange = (licenseIdx: number, iso2: string, option: any) => {
    let countryId = option?.value;
    if (typeof countryId === 'string') {
      countryId = parseInt(countryId, 10);
    }
    updateLicenseFields(licenseIdx, {
      country: iso2,
      countryId,
      countryName: option?.children,
      state: '',
      stateId: undefined,
      stateName: undefined,
    });
    if (typeof countryId === 'number' && !isNaN(countryId)) {
      GetState(countryId).then((result: any) => {
        setLicenseStates((prev) => {
          const newStates = [...prev];
          newStates[licenseIdx] = result;
          return newStates;
        });
      });
    }
  };

  // Handler for license state change
  const handleLicenseStateChange = (licenseIdx: number, stateCode: string, option: any) => {
    const stateId = typeof option?.value === 'number' ? option.value : parseInt(option?.value);
    updateLicenseFields(licenseIdx, { state: stateCode, stateId, stateName: option?.children });
  };

  // When licenses are added/removed, sync licenseStates array
  useEffect(() => {
    setLicenseStates((prev) => {
      if (licenses?.length > prev?.length) {
        return [...prev, []];
      } else if (licenses?.length < prev?.length) {
        return prev.slice(0, licenses?.length);
      }
      return prev;
    });
  }, [licenses?.length]);

  useEffect(() => {
    GetCountries().then((result: any) => {
      setCountriesList(result);
    });
  }, []);

  // Set initial form values for preferences
  useEffect(() => {
    form.setFieldsValue({
      emailPreference: "1",
      pagerPreference: "1",
      primaryPhonePreference: "1",
      secondaryPhonePreference: "4"
    });
  }, [form]);

  // Populate form with user details when in edit mode
  useEffect(() => {
    if (isEditMode && userDetailsData && !isUserDetailsLoading && licenseCountries.length > 0) {
      populateFormWithUserDetails(userDetailsData);
    }
  }, [userDetailsData, isEditMode, isUserDetailsLoading, licenseCountries]);

  const populateFormWithUserDetails = (userData: any) => {
    // Populate basic user information
    form.setFieldsValue({
      firstName: userData.first_name || '',
      lastName: userData.last_name || '',
      displayName: userData.display_name || '',
      username: userData.username || '',
      email: userData.email || '',
      emailPreference: userData.email_pref?.toString() || '1',
      pager: userData.pager_number || '',
      pagerPreference: userData.pager_pref?.toString() || '1',
    });

    // Populate roles and specialties
    if (userData.network_roles?.length > 0) {
      const roleIds = userData.network_roles.map((role: any) => role.id);
      setSelectedRole(roleIds);
      form.setFieldsValue({ role: roleIds });
      
      // Fetch specialties for the selected roles
      if (roleIds.length > 0) {
        dispatch(fetchONSpecialities({ roleIDs: roleIds.join(",") }));
      }
    }

    if (userData.network_specialties?.length > 0) {
      const specialtyIds = userData.network_specialties.map((specialty: any) => specialty.id);
      setSelectedSpecialty(specialtyIds);
      form.setFieldsValue({ specialty: specialtyIds });
    }

    // Populate medical licenses
    if (userData.medical_licenses?.length > 0) {
      const licenseData = userData.medical_licenses; // Get the first array of licenses
      if (Array.isArray(licenseData)) {
        const newLicenses = licenseData.map((license: any) => {
          // Find country ID from countries list
          const countryObj = licenseCountries.find((c: any) => c.iso2 === license.country?.country_name_code);

          return {
            country: license.country?.country_name_code || '',
            countryId: countryObj?.id,
            countryName: license.country?.name || '',
            state: license.issuing_state?.state_code || '',
            stateId: undefined, // Will be set after fetching states
            stateName: license.issuing_state?.name || '',
            licenseNumber: license.license_number || '',
          };
        });
        setLicenses(newLicenses);
        
        // Update form values with license data
        newLicenses.forEach((license: any, index: number) => {
          form.setFieldValue(['licenses', index, 'country'], license.country);
          form.setFieldValue(['licenses', index, 'countryId'], license.countryId);
          form.setFieldValue(['licenses', index, 'countryName'], license.countryName);
          form.setFieldValue(['licenses', index, 'state'], license.state);
          form.setFieldValue(['licenses', index, 'stateId'], license.stateId);
          form.setFieldValue(['licenses', index, 'stateName'], license.stateName);
          form.setFieldValue(['licenses', index, 'licenseNumber'], license.licenseNumber);
        });
        
        // Fetch states for each license
        newLicenses.forEach((license: any, index: number) => {
          if (license.countryId) {
            GetState(license.countryId).then((result: any) => {
              setLicenseStates((prev) => {
                const newStates = [...prev];
                newStates[index] = result;
                return newStates;
              });
              
              // Find state ID after states are loaded
              const stateObj = result.find((s: any) => s.state_code === license.state);
              if (stateObj) {
                updateLicenseFields(index, { stateId: stateObj.id });
                const updatedLicense = newLicenses
                updatedLicense[index].stateId = stateObj.id
                setLicenses(updatedLicense)
                // Also update the form value
                form.setFieldValue(['licenses', index, 'stateId'], stateObj.id);
              }
            });
          }
        });
      }
    }

    // Populate contact information
    if (userData.contact_info?.length > 0) {
      const primaryContact = userData.contact_info.find((contact: any) => contact.number_status === 'primary');
      const secondaryContact = userData.contact_info.find((contact: any) => contact.number_status === 'secondary');

      if (primaryContact) {
        setPrimaryNumber(primaryContact.number);
        setSelectedPrimaryCountry('us'); // Default, will be updated based on country code
        form.setFieldsValue({
          primaryPhone: {
            number: primaryContact.number,
            code: primaryContact.country_code?.replace('+', '') || '1'
          },
          primaryPhonePreference: primaryContact.pref?.toString() || '1'
        });
      }

      if (secondaryContact) {
        setSecondaryNumber(secondaryContact.number);
        setSelectedSecondaryCountry('us'); // Default, will be updated based on country code
        form.setFieldsValue({
          secondaryPhone: {
            number: secondaryContact.number,
            code: secondaryContact.country_code?.replace('+', '') || '1'
          },
          secondaryPhonePreference: secondaryContact.pref?.toString() || '4'
        });
      }
    }

    // Populate practice sites
    if (userData.practice_sites?.length > 0) {
      const practiceData = userData.practice_sites.map((site: any) => ({
        practiceName: site.name || '',practicePosition:site.practicePosition,
        // optionalPhone: site.phone_number || '',
        // optionalPhoneCode: site.phone_number_country_code?.replace('+', '') || '',
        country: site.country?.country_name_code || '',
        countryName: site.country?.name || '',
        state: site.state?.state_code || '',
        stateName: site.state?.name || '',
        city: site.city?.name || '',
        cityName: site.city?.name || '',
        cityId: site.city?.city_code || '',
        address: site.address_one || '',
        address2: site.address_two || '',
        address3: site.address_three || '',
        zipcode:site.zipcode || ''
      }));
      setPracticeDetails(practiceData);

      // Align phone input countries with practice site countries
      // setSelectedPracticeCountries(practiceData.map((p: any) => p.country || 'us'));

      // Set known field values into the form per row
      practiceData.forEach((practice: any, idx: number) => {
        form.setFieldsValue({ [`practiceName_${idx}`]: practice.practiceName });
        form.setFieldsValue({ [`practicePosition${idx}`]: practice.practiceName });
        // form.setFieldsValue({ [`optionalPhone_${idx}`]: practice.optionalPhone });
        form.setFieldsValue({ [`address_${idx}`]: practice.address });
        form.setFieldsValue({ [`address2_${idx}`]: practice.address2 });
        form.setFieldsValue({ [`zipcode${idx}`]: practice.zipcode });
        form.setFieldsValue({ [`country_${idx}`]: practice.country || undefined });
        form.setFieldsValue({ [`state_${idx}`]: practice.state || undefined });
      });

      // Load state and city lists and set city field via setFieldsValue
      practiceData.forEach((practice: any, idx: number) => {
        const countryObj = countriesList.find((c: any) => c.iso2 === practice.country);
        if (countryObj) {
          GetState(countryObj.id).then((states: any) => {
            setPracticeStateLists(prev => {
              const newArr = [...prev];
              newArr[idx] = states;
              return newArr;
            });

            const stateObj = states.find((s: any) => s.state_code === practice.state);
            if (stateObj) {
              GetCity(countryObj.id, stateObj.id).then((cities: any) => {
                setPracticeCityLists(prev => {
                  const newArr = [...prev];
                  newArr[idx] = cities;
                  return newArr;
                });

                const cityObj = cities.find((c: any) => c.name === practice.cityName);
                if (cityObj) {
                  updatePracticeDetail(idx, 'cityId', cityObj.id);
                  updatePracticeDetail(idx, 'city', cityObj.name);
                  updatePracticeDetail(idx, 'cityName', cityObj.name);
                  form.setFieldsValue({ [`city_${idx}`]: cityObj.id });
                }
              });
            }
          });
        }
      });
    }

    // Set profile picture if exists
    if (userData.profile_pic) {
      setBase64Image(userData.profile_pic);
    }
  };

  const handleCountrySelect = (value: string, option: any) => {
    setSelectedCountryCode(value);
    setSelectedCountryId(parseInt(option.key));
    GetState(parseInt(option.key)).then((result: any) => {
      setStateList(result);
      setCityList([]);
    });
    form.setFieldsValue({ state: undefined, city: undefined });
  };

  const handleStateSelect = (value: string, option: any) => {
    setSelectedStateId(parseInt(option.key));
    if (selectedCountryId) {
      GetCity(selectedCountryId, parseInt(option.key)).then((result: any) => {
        setCityList(result);
      });
    }
    form.setFieldsValue({ city: undefined });
  };

  const handleCitySelect = (value: string, option: any, idx?: number) => {
    // value is cityId, option contains cityName
    if (typeof idx === 'number') {
      updatePracticeDetail(idx, 'city', option?.children ?? '');
      updatePracticeDetail(idx, 'cityName', option?.children ?? '');
      updatePracticeDetail(idx, 'cityId', value); // <-- Store cityId
    }
  };

  const steps = isEditMode ? [
    {
      title: 'General Details',
      icon: <UserOutlined />,
    },
    {
      title: 'Contact Details',
      icon: <PhoneOutlined />,
    },
  ] : [
    {
      title: 'General Details',
      icon: <UserOutlined />,
    },
    {
      title: 'Contact Details',
      icon: <PhoneOutlined />,
    },
    {
      title: 'Payment and Submit',
      icon: <CreditCardOutlined />,
    },
  ];

  const addLicense = () => {
    setLicenses([...licenses, { country: '', countryId: undefined, state: '', stateId: undefined, licenseNumber: '' }]);
  };

  const removeLicense = (index: number) => {
    if (licenses?.length > 1) {
      const newLicenses = licenses.filter((_, i) => i !== index);
      setLicenses(newLicenses);
    }
  };

  const updateLicense = (index: number, field: keyof LicenseInfo, value: string | number | undefined) => {
    const newLicenses = [...licenses];
    newLicenses[index] = { ...newLicenses[index], [field]: value };
    setLicenses(newLicenses);
  };

  const [registerdUserId,setRegisteredUserId] = useState<any>("")
  const [userPayload, setUserPayload] = useState({});

  const nextStep = () => {
    setHasInteracted(true);

    form.validateFields().then(async (values:any) => {
      let payload:any ={}
      if(currentStep === 0){
        const medicalLicenses = values?.licenses?.map((item:any)=>{
          return {
            license_number: item?.licenseNumber ?? '',
            is_valid:true,
            country: {
              name: item?.countryName ?? "",
              country_name_code: item?.country ?? "",
            },
            issuing_state: {
              name:item?.stateName ?? "",
              state_code: item?.state ?? ""
          },
          }
        })
        payload = {
          first_name: values?.firstName ?? '',
          last_name: values?.lastName ?? '',
          role_ids: values?.role ?? '',
          specialty_ids: values?.specialty ?? '',
          medical_licenses: medicalLicenses?? [],
        }
        setUserPayload(payload); // Save to state
        setCurrentStep(currentStep + 1);
      }else if(currentStep === 1){
        const practiceSites = practiceDetails?.map((detail:any)=>
          {
         return {name: detail?.practiceName, practice_position:detail?.practicePosition,
          phone_number_country_code: detail?.optionalPhoneCode ?? "",
          phone_number: detail?.optionalPhone ?? "",
          country: {
              name:detail?.countryName,
              country_name_code: detail?.country
          },
          state: {
              name:detail?.stateName,
              state_code: detail?.state
          },
          city: {
              name: detail?.city,
              city_code: detail?.cityId // <-- Use cityId for city_code
          },
          address_one:  detail?.address ?? "",
          address_two: detail?.address2 ?? "",
          zipcode: detail?.zipcode ?? ""}
      })
        payload= {
          ...userPayload, // Merge previous step's data
          display_name: values?.displayName ?? "",
          username: values?.username ?? "",
          email: values?.email ?? "",
          email_pref: values?.emailPreference ?? "",
          pager_number: values?.pager ? values?.pager : null,
          pager_pref: values?.pager? values?.pagerPreference : null,
          profile_picture:values?.profilePicture ? {
              filename: values?.profilePicture?.split("\\").pop() ?? "" ,
              content_type: `image/${values?.profilePicture?.split('.')?.pop()?.toLowerCase()}`,
              file_data: base64Image ?? ""
          }:null,
          source_of_reg_request: "sap",
          contact_info: [
              {
                  country_code: `+${values?.primaryPhone?.code}`,
                  number: values?.primaryPhone?.number?.replace(new RegExp(`^\\+?${values?.primaryPhone?.code}`), ''),
                  pref: values?.primaryPhonePreference,
                  number_type: "mobile",
                  number_status: "primary"
              },
              ...(values?.secondaryPhone?.number ? [{
                  country_code: `+${values?.secondaryPhone?.code}`,
                  number: values?.secondaryPhone?.number?.replace(new RegExp(`^\\+?${values?.secondaryPhone?.code}`), ''),
                  pref: values?.secondaryPhonePreference ?? "",
                  number_type: "mobile",
                  number_status: "secondary"
              }] : [])
          ],
          practice_sites: practiceSites?? [],
        }
        
        if (isEditMode && userId) {
          // Update existing user
          const editPayload = {
            ...payload,
            user_id: userId
          };
          const response = await dispatch(editONRequest(editPayload))
          if(response?.payload?.isSuccess){
            toast.success(response?.payload?.message)
            navigate(-1); // Go back to previous page
          }else{
            toast.error(response?.payload)
          }
        } else {
          // Create new user
          const response = await dispatch(addONUser(payload))
          if(response?.payload?.isSuccess){
            setRegisteredUserId(response?.payload?.result?.id)
            setCurrentStep(currentStep + 1);
            toast.success(response?.payload?.message)
          }else{
            toast.error(response?.payload)
          }
        }
      }
    }).catch((info) => {
      console.log('Validate Failed:', info);
    });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleCancel = () => {
      // Navigate back to the previous route
      navigate(-1);
  };

  const onFinish = (values: any) => {
    message.success('Registration completed successfully!');
  };

  // Sync licenses state with form for validation
  useEffect(() => {
    form.setFieldsValue({ licenses });
  }, [licenses, form]);

  

  const handleRoleChange = (value: any) => {
    setSelectedRole(value);
    dispatch(fetchONSpecialities({roleIDs:value.join(",")}))
  };

  const handleSpecialtyChange = (value: any) => {
    setSelectedSpecialty(value);
  };

  // to handle select all action from multi select
  const handleSelectAll = (
    e: any,
    handleChange: any,
    options: any,
    name?: string
  ) => {
    // if select all clicked then it will call the appropriate handle function
    // then update selected value
    if (e.target.checked) {
      handleChange(
        options.map((data: any) => data.id),
        ""
      );
      if (name) {
        form.setFieldValue(
          name,
          options.map((data: any) => data.id)
        );
      }
    } else {
      if (name) {
        form.setFieldValue(name, []);
      }
      handleChange([], "");
    }
  };

   // To render custom tage on multi select
   const tagRender = (props: any) => {
    const { label, value, closable, onClose } = props;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <Tag
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3,padding: ".25rem",
           }}
      >
        {label?.props?.children ? label?.props?.children : null}
      </Tag>
    );
  };

  // for check box options in multi select
  const TextWithCheckbox = (props: any) => {
    return (
      <div>
        <Checkbox
          checked={props.checked}
          style={{ margin: "0 .5rem 0 0rem" }}
        />
        {props.children}
      </div>
    );
  };

  const [base64Image, setBase64Image] = useState<any>('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleImageSelect = (event:any) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      
      if (file.size > maxSize) {
        toast.error('File size must be less than 10MB');
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      convertToBase64(file)
        .then(base64 => {
          setBase64Image(base64);
        })
        .catch(error => {
          console.error('Error converting to base64:', error);
        });
    }
  };

  const convertToBase64 = (file:any) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // this reads file as base64
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };



  const renderGeneralDetails = () => (
    <div className="step-content">
      {/* Hidden Form.Item for validating licenses array */}
      <Form.Item shouldUpdate noStyle>
        {() => (
          <Form.Item
            name="licenses"
            rules={[{
              validator: () => {
                if (!licenses?.length) {
                  return Promise.reject("At least one medical license is required.");
                }
                for (const lic of licenses) {
                  if (!lic.countryId || !lic.stateId || !lic.licenseNumber) {
                    return Promise.reject("All license fields are required.");
                  }
                }
                return Promise.resolve();
              },
            }]}
            style={{ display: "none" }}
          >
            <Input />
          </Form.Item>
        )}
      </Form.Item>
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              { required: true, message: 'Please enter first name' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return /^[A-Za-z]+$/.test(String(value).trim())
                    ? Promise.resolve()
                    : Promise.reject('Only letters are allowed');
                }
              }
            ]}
          >
            <Input placeholder="Enter first name" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              { required: true, message: 'Please enter last name' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return /^[A-Za-z]+$/.test(String(value).trim())
                    ? Promise.resolve()
                    : Promise.reject('Only letters are allowed');
                }
              }
            ]}
          >
            <Input placeholder="Enter last name" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select
              mode="multiple"
              style={{
                width: "100%",
                height:"3rem"
              }}
              showSearch
              optionFilterProp="label"
              className='role-select'
              onChange={handleRoleChange}
              placeholder={"Select Roles"}
              maxTagCount={"responsive"}
              value={selectedRole}
              virtual={false}
              tagRender={tagRender}
              dropdownRender={(menu) => (
                <>
                  <Space style={{ padding: "5px 12px 4px" }}>
                    <Checkbox
                      checked={selectedRole?.length === ONRolesListData?.length}
                      onChange={(e) =>
                        handleSelectAll(e, handleRoleChange, ONRolesListData, "role")
                      }
                    >
                      Select All
                    </Checkbox>
                  </Space>
                  <Divider style={{ margin: "8px 0" }} />
                  {menu}
                </>
              )}
            >
              {ONRolesListData?.map((item: any) => (
                <Option key={item.id} value={item.id} label={item?.name}>
                  <TextWithCheckbox checked={selectedRole?.includes(item?.id)}>
                    {item?.name}
                  </TextWithCheckbox>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="specialty"
            label="Specialty"
            rules={[{ required: true, message: 'Please select specialty' }]}
          >
            <Select
              mode="multiple"
              disabled={ONSpecialitiesListData?.length===0}
              style={{
                width: "100%",
                height: "3rem",
              }}
              showSearch
              optionFilterProp="label"
              className='specialty-select'
              onChange={handleSpecialtyChange}
              placeholder={"Select Specialty"}
              maxTagCount={"responsive"}
              value={selectedSpecialty}
              virtual={false}
              tagRender={tagRender}
              dropdownRender={(menu) => (
                <>
                  <Space style={{ padding: "5px 12px 4px" }}>
                    <Checkbox
                      checked={selectedSpecialty?.length === ONSpecialitiesListData?.length}
                      onChange={(e) =>
                        handleSelectAll(e, handleSpecialtyChange, ONSpecialitiesListData, "specialty")
                      }
                    >
                      Select All
                    </Checkbox>
                  </Space>
                  <Divider style={{ margin: "8px 0" }} />
                  {menu}
                </>
              )}
            >
              {ONSpecialitiesListData?.map((item: any) => (
                <Option key={item.id} value={item.id} label={item?.name}>
                  <TextWithCheckbox checked={selectedSpecialty?.includes(item?.id)}>
                    {item?.name}
                  </TextWithCheckbox>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" style={{color: "var(--font-color-primary)"}}>
        Medical License Information <Text type="danger">*</Text>
      </Divider>
      
      {licenses.map((license, index) => (
        <Card key={index} className="license-card" size="small">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={8}>
              <Form.Item
                name={['licenses', index, 'countryId']}
                label="Country"
                rules={[{ required: true, message: 'Please select country' }]}
              >
                <Select
                  placeholder="Select country"
                  value={license.countryId}
                  onChange={(value, option) => {
                    const iso2 = (option as any)?.props?.['data-iso2'] || '';
                    handleLicenseCountryChange(index, iso2, option);
                  }}
                  size="large"
                  showSearch
                  optionFilterProp="children"
                >
                  {licenseCountries.map((country: any) => (
                    <Option value={country.id} key={country.id} data-iso2={country.iso2}>{country.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name={['licenses', index, 'stateId']}
                label="Issuing State"
                rules={[{ required: true, message: 'Please select state' }]}
              >
                <Select
                  placeholder="Select state"
                  value={license.stateId}
                  onChange={(value, option) => {
                    const stateCode = (option as any)?.props?.['data-statecode'] || '';
                    handleLicenseStateChange(index, stateCode, option);
                  }}
                  size="large"
                  showSearch
                  optionFilterProp="children"
                  disabled={!license.countryId}
                >
                  {(licenseStates[index] || []).map((state: any) => (
                    <Option value={state.id} key={state.id} data-statecode={state.state_code}>{state.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name={['licenses', index, 'licenseNumber']}
                label="License Number"
                rules={[{ required: true, message: 'Please enter license number' }]}
              >
                <Input
                  placeholder="Enter license number"
                  value={license.licenseNumber}
                  onChange={(e) => updateLicense(index, 'licenseNumber', e.target.value)}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={2}>
              <Space direction="vertical" className="license-actions">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<PlusOutlined />}
                  onClick={addLicense}
                  className="add-license-btn"
                />
                {licenses?.length > 1 && (
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<MinusOutlined />}
                    onClick={() => removeLicense(index)}
                    className="remove-license-btn"
                    danger
                  />
                )}
              </Space>
            </Col>
          </Row>
        </Card>
      ))}
    </div>
  );

  

  const renderContactDetails = () => {
    const handlePrimaryNumberChange = (phoneNumber:any) =>{
      setPrimaryNumber(phoneNumber);
      const primaryCountryData = primaryNumberRef?.current
        ?.getInstance()
        ?.getSelectedCountryData();
      form.setFieldsValue({ primaryPhone: {
        number:phoneNumber,
        code:primaryCountryData?.dialCode
      } })
    }

    const handleSecondaryNumberChange = (phoneNumber:any) =>{
      setSecondaryNumber(phoneNumber);
      const primaryCountryData = secondaryNumberRef?.current
        ?.getInstance()
        ?.getSelectedCountryData();
        form.setFieldsValue({ secondaryPhone: {
        number:phoneNumber,
        code:primaryCountryData?.dialCode
      } })
    }

    return(
    <div className="step-content">
      <Divider orientation="left" style={{color: "var(--font-color-primary)",borderColor:"var(--font-color-primary)"}}>
          Practitioner Details
      </Divider>
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="displayName"
            label="Display Name"
            rules={[
              { required: true, message: 'Please enter display name' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return /^[A-Za-z]+$/.test(String(value).trim())
                    ? Promise.resolve()
                    : Promise.reject('Only letters are allowed');
                }
              }
            ]}
          >
            <Input placeholder="Enter first name" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="username"
            label="Username"
            style={{opacity:isEditMode? 0.6:1, pointerEvents:isEditMode?"none":"auto"}}

            rules={[
              { required: true, message: 'Please enter username' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  return /^[A-Za-z]+$/.test(String(value).trim())
                    ? Promise.resolve()
                    : Promise.reject('Only letters are allowed');
                }
              }
            ]}
          >
            <Input placeholder="Enter last name" size="large" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} sm={8} lg={9}>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
          >
            <Input placeholder="Enter email address" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4} lg={3}>
          <Form.Item
            name="emailPreference"
            label="Preference"
          >
            <Select defaultValue="1" size="large">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8} lg={9}>
          <Form.Item
            name="pager"
            label="Pager Number"
          >
            <Input placeholder="Enter pager number" size="large" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={4} lg={3}>
          <Form.Item
            name="pagerPreference"
            label="Preference"
          >
            <Select defaultValue="1" size="large">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12} lg={9}>
          <Form.Item
            label="Primary Phone"
            name="primaryPhone"
            rules={[
              {
                validator: (_, value) =>
                  isPrimaryPhoneNumberValid || !form.getFieldValue('primaryPhone')
                    ? Promise.resolve()
                    : Promise.reject("Please enter a valid number"),
              },
              { required: true, message: 'Please enter primary phone' },
            ]}
          >
            <div>
              <IntlTelInput
                ref={primaryNumberRef}
                initialValue={primaryNumber || ''}
                onChangeNumber={handlePrimaryNumberChange}
                onChangeValidity={(isValid: any) => setIsPrimaryPhoneNumberValid(isValid)}
                onChangeCountry={(country: any) => {
                  if (selectedPrimaryCountry !== country) {
                    primaryNumberRef?.current?.getInstance()?.setNumber("");
                  }
                  setSelectedPrimaryCountry(country);
                }}
                initOptions={{
                  separateDialCode: true,
                  strictMode: true,
                  initialCountry: selectedPrimaryCountry,
                  utilsScript:
                    "https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.10/build/js/utils.js",
                }}
              />
            </div>
          </Form.Item>
        </Col>
        <Col xs={24} sm={3}>
          <Form.Item
            name="primaryPhonePreference"
            label="Preference"
          >
            <Select defaultValue="1" size="large">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={9}>
          <Form.Item
            label="Secondary Phone"
            name="secondaryPhone"
            rules={[
              {
                validator: (_, value) =>
                  isSecondaryPhoneNumberValid || !secondaryNumber
                    ? Promise.resolve()
                    : Promise.reject("Please enter a valid number"),
              },
            ]}
          >
            <div>
              <IntlTelInput
                ref={secondaryNumberRef}
                initialValue={secondaryNumber}
                onChangeNumber={handleSecondaryNumberChange}
                onChangeValidity={(isValid: any) => setIsSecondaryPhoneNumberValid(isValid)}
                onChangeCountry={(country: any) => {
                  if (selectedSecondaryCountry !== country) {
                    setSelectedSecondaryCountry(country);
                    setSecondaryNumber("");
                  } else {
                    setSelectedSecondaryCountry(country);
                  }
                }}
                initOptions={{
                  separateDialCode: true,
                  strictMode: true,
                  initialCountry: selectedSecondaryCountry,
                  utilsScript:
                    "https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.10/build/js/utils.js",
                }}
              />
            </div>
          </Form.Item>
        </Col>
        <Col xs={24} sm={3}>
          <Form.Item
            name="secondaryPhonePreference"
            label="Preference"
          >
            <Select defaultValue="4" size="large">
              <Option value="1">1</Option>
              <Option value="2">2</Option>
              <Option value="3">3</Option>
              <Option value="4">4</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Practice Details Section */}
      <Divider orientation="left" style={{color: "var(--font-color-primary)",borderColor:"var(--font-color-primary)"}}>Practice Details</Divider>
      {practiceDetails.map((detail, idx) => (
        <React.Fragment key={idx}>
<Row gutter={[24, 16]}>
  {/* Practice Name */}
  <Col xs={24} sm={8} lg={8}>
    <Form.Item
      name={[`practiceName_${idx}`]}
      label="Practice Name"
      rules={[{ required: true, message: 'Please enter practice name' }]}
    >
      <Input
        placeholder="Enter practice name"
        size="large"
        value={detail.practiceName}
        onChange={e => updatePracticeDetail(idx, 'practiceName', e.target.value)}
      />
    </Form.Item>
  </Col>

  {/* Practice Position */}
  <Col xs={24} sm={8} lg={8}>
    <Form.Item
      name={[`practicePosition_${idx}`]} // Fixed name key
      label="Practice Position"
      rules={[{  message: 'Please enter practice position' }]}
    >
      <Input
        placeholder="Enter practice position"
        size="large"
        value={detail.practicePosition}
        onChange={e => updatePracticeDetail(idx, 'practicePosition', e.target.value)}
      />
    </Form.Item>
  </Col>

  {/* Phone Number (Optional) */}
  <Col xs={24} sm={6} lg={6}>
    <Form.Item
      name={[`optionalPhone_${idx}`]}
      label="Phone Number (Optional)"
      // rules={[
      //   {
      //     validator: (_, value) =>
      //       isPracticePhoneNumbersValid[idx] || !value
      //         ? Promise.resolve()
      //         : Promise.reject("Please enter a valid number"),
      //   },
      // ]}
      getValueFromEvent={(phoneNumber) => phoneNumber}
      getValueProps={value => ({ value: value || "" })}
    >
      <IntlTelInput
        key={`${idx}-${selectedPracticeCountries[idx]}`}
        ref={el => {
          if (practicePhoneNumberRefs.current) {
            practicePhoneNumberRefs.current[idx] = el;
          }
        }}
        initialValue={form.getFieldValue(`optionalPhone_${idx}`) || ''}
        onChangeNumber={phoneNumber => form.setFieldsValue({ [`optionalPhone_${idx}`]: phoneNumber })}
        // onChangeCountry={country => handlePracticePhoneCountryChange(idx, country)}
        initOptions={{
          separateDialCode: true,
          strictMode: true,
          initialCountry: selectedPracticeCountries[idx],
          utilsScript:
            "https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.10/build/js/utils.js",
        }}
      />
    </Form.Item>
  </Col>

  {/* Add/Remove Buttons */}
  <Col xs={24} sm={2} lg={2} style={{ textAlign: 'left',marginTop:30 }}>
    <Space direction="vertical" className="practice-actions">
      {practiceDetails?.length > 1 && (
        <Button
          type="primary"
          shape="circle"
          icon={<MinusOutlined />}
          size="large"
          danger
          onClick={() => removePracticeDetail(idx)}
          className="remove-practice-btn"
        />
      )}
      {idx === practiceDetails?.length - 1 && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          size="large"
          className="add-practice-btn"
          onClick={addPracticeDetail}
        />
      )}
    </Space>
  </Col>
</Row>
          <Row gutter={[24, 16]}>
            <Col xs={24}>
              <Form.Item
                label="Location"
                required
              >
                <Row gutter={[8, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`country_${idx}`]}
                      rules={[
                        {
                          required: true,
                          message: 'Please select country',
                          validator: (_, value) => {
                            if (!hasInteracted) return Promise.resolve();
                            if (!value) return Promise.reject('Please select country');
                            return Promise.resolve();
                          }
                        }
                      ]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <Select
                        placeholder="Select country"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        value={detail.country || undefined}
                        onChange={(value,option:any) => handlePracticeCountryChange(idx, value,option?.children)}
                        allowClear
                      >
                        {countriesList.map((country: any) => (
                          <Option value={country.iso2} key={country.id}>{country.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`state_${idx}`]}
                      rules={[
                        {
                          required: true,
                          message: 'Please select state',
                          validator: (_, value) => {
                            if (!hasInteracted) return Promise.resolve();
                            if (!value) return Promise.reject('Please select state');
                            return Promise.resolve();
                          }
                        }
                      ]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <Select
                        placeholder="Select state"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        value={detail.state || undefined}
                        onChange={(value,option:any) => handlePracticeStateChange(idx, value,option?.children)}
                        disabled={!detail.country}
                        allowClear
                      >
                        {(practiceStateLists[idx] || []).map((state: any) => (
                          <Option value={state.state_code} key={state.id}>{state.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`city_${idx}`]}
                      rules={[
                        {
                          required: true,
                          message: 'Please select city',
                          validator: (_, value) => {
                            if (!hasInteracted) return Promise.resolve();
                            if (!value) return Promise.reject('Please select city');
                            return Promise.resolve();
                          }
                        }
                      ]}
                      validateTrigger={['onChange', 'onBlur']}
                    >
                      <Select
                        placeholder="Select city"
                        size="large"
                        showSearch
                        optionFilterProp="children"
                        value={detail.city || undefined}
                        onChange={(value,option:any) => handleCitySelect(value, option, idx)}
                        disabled={!detail.state}
                        allowClear
                      >
                        {(practiceCityLists[idx] || []).map((city: any) => (
                          <Option value={city.id} key={city.id}>{city.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]}>
            <Col xs={24}>
              <Form.Item
                label="Addresses"
                required
              >
                <Row gutter={[8, 0]}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`address_${idx}`]}
                      rules={[{ required: true, message: 'Please enter address line 1' }]}
                      noStyle
                    >
                      <Input
                        placeholder="Enter address line 1"
                        size="large"
                        value={detail.address}
                        onChange={e => updatePracticeDetail(idx, 'address', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`address2_${idx}`]}
                      noStyle
                    >
                      <Input
                        placeholder="Enter address line 2 (optional)"
                        size="large"
                        onChange={e => updatePracticeDetail(idx, 'address2', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name={[`zipcode${idx}`]}
                      noStyle
                    >
                      <Input
                        placeholder="Enter zip code (optional)"
                        size="large"
                        onChange={e => updatePracticeDetail(idx, 'zipcode', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
          </Row>
        </React.Fragment>
      ))}

      {/* Profile Picture Upload Section */}
      {/* <Divider orientation="left" style={{color: "var(--font-color-primary)"}}>
        Profile Picture
      </Divider> */}
      <Row  justify="center" align="middle" gutter={[24, 16]} style={{marginTop:"1rem"}} >
        <Col xs={24} lg={12}>
          <Form.Item
            name="profilePicture"
            
            // label="Upload Profile Pic"
          >
            <div className="profile-upload-container">
              <div className="upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
                id="imageInput"
                ref={fileInputRef}
              />
                <Text className="upload-text">Upload Profile Pic (JPEG, PNG)</Text>
                <label htmlFor="imageInput">
                  <Button type="primary" className="upload-button" onClick={() => fileInputRef.current?.click()}>
                    Upload file
                  </Button>
                </label>
                {base64Image && (
        <div>
          <p style={{color:"var(--font-color-primary)",marginTop:".5rem"}}>Preview:</p>
          <img src={base64Image} alt="Preview" style={{ width: 200, height: 'auto' }} />
        </div>)}
              </div>
            </div>
          </Form.Item>
        </Col>
      </Row>

      {/* Terms and Conditions Section */}
      {/* <Divider orientation="left" style={{color: "var(--font-color-primary)"}}>
        Terms and Conditions
      </Divider> */}
      {!isEditMode && (
        <Row gutter={[24, 16]}>
          <Col xs={24} className='terms-conditions-form-item-col'>
            <Form.Item
              name="termsAccepted"
              valuePropName="checked"
              
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject('Please accept the terms and conditions'),
                },
              ]}
            >
              <Checkbox>
                I accept and agree the Terms of service and Privacy policy
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
      )}
    </div>
  );}


  const renderPaymentStep = () => {

    const handleMemeberShipRegistration =async (planId:string) =>{
      const payload = {
        user_id:registerdUserId,
        subscription_id:planId,
        payment_mode: "free",
        is_paid: false,
        billing_date: null,
        source_of_mm_plan_request:"sap"
      }
      const response =await dispatch(registerMembership(payload))
      if(response?.payload?.isSuccess){
        toast.success(response?.payload?.message)
        navigate(-1);
      }else{
        toast.error(response?.payload?.message)
      }
    }
    
    return(
    <div className="step-content">
      <Card className="pricing-card">
        <Title level={3} className="pricing-title">Upgrade your plan</Title>
        <Row gutter={[24, 24]} className="pricing-row">
          {
            mombershipPlanListData?.map((plan:any)=>{
              return(
                <Col xs={24} md={8}>
                  <Card className="plan-card free-plan">
                    <Title level={4}>{plan?.name}</Title>
                    <Text className="price">{`${plan?.price} ${plan?.currency}/${plan?.billing_cycle}`}</Text>
                    <Button type="primary" className="plan-button" onClick={()=>handleMemeberShipRegistration(plan?.id)}>Start </Button>
                    <ul className="features-list">
                      {
                        plan?.active_features?.map((feature:any)=>{
                          return(
                            <li className="included">{feature?.name}</li>
                          )
                        })
                      }
                    </ul>
                  </Card>
                </Col>
              )
            })
          }
        </Row>
      </Card>
    </div>
  );}

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderGeneralDetails();
      case 1:
        return renderContactDetails();
      case 2:
        return isEditMode ? null : renderPaymentStep();
      default:
        return null;
    }
  };

  // Show loading state when fetching user details in edit mode
  if (isEditMode && isUserDetailsLoading) {
    return (
      <div className="add-user-container">
        <Card className="registration-card">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <div>Loading user details...</div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="add-user-container">
      {isEditMode && (
        <div className="registration-header">
          <Title level={2} className="main-title">Edit User</Title>
          <Text className="subtitle">Update user information</Text>
        </div>
      )}

      <Card className="registration-card">
        <Steps
          current={currentStep}
          items={steps}
          className="registration-steps"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="registration-form"
          validateTrigger={['onChange', 'onBlur']}
          validateMessages={{
            required: '${label} is required',
          }}
        >
          {renderStepContent()}

          <div className="form-actions">
            <Space>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>
              {currentStep > 0 && (
                <Button onClick={prevStep} size="large">
                  Previous
                </Button>
              )}
              {(currentStep < steps?.length - 1 || isEditMode) && (
                <Button
                  type="primary"
                  onClick={nextStep}
                  size="large"
                  loading={userRegisterationIsLoading || editRequestIsLoading}
                  disabled={userRegisterationIsLoading || editRequestIsLoading}
                  className='next-btn'
                >
                  {isEditMode && currentStep === steps?.length - 1 ? 'Update' : 'Next'}
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Card>
    </div>
  );
};
